import { AnyAction } from 'redux';
import { TenantData } from '../types';
import { OffersState } from '../types/state';
import produce from 'immer';
import { ALL, SummaryTableDataFields } from '../constants';
import { orderBy, get } from 'lodash';
import { TenantFlagItem } from '../types/flag.model';
// import { flagDetails } from '../actions/schema';

// import update from 'immutability-helper';
const initialState: OffersState = {
  searchResults: undefined,
  isLoading: false,
  dashboardData: undefined,
  kpiData: undefined,
  selectedTenant: undefined,
  selectedSearchResult: undefined,
  selectedOfferCycle: undefined,
  offerCycles: undefined,
  selectedStrategy: undefined,
  propertyOptions: [],
  chargeOptions: undefined,
  summaryData: undefined,
  rangeFilter: undefined,
  rangeMapping: undefined,
  selectedOfferFilter: 'InReview',
  enableEdit: true,
  showAddOfferDialog: false,
  selectedView: 'list', // list or table
  showFilterSettingsDrawer: false,
  stickinessFilterState: undefined,
  renewalSummary: undefined,
  activeStrategy: undefined,
  offerEditMode: undefined,
  uploadToPMS: {
    isOpen: false,
    isLoading: false,
    data: [],
  },
};

const calculateSummaryTableData = (
  dashboardData: TenantData[],
  selectedProperty: string
) => {
  let rangeMapping: any = {};

  let updateRangeMapping = (key: string, value: string) => {
    if (rangeMapping.hasOwnProperty(key)) rangeMapping[key].push(value);
    else rangeMapping[key] = [value];
  };

  let getValues = (data: any, row: any) => {
    // Uses Best Offered Rent vs Current rent ((OfferedRent - CurrentRent) /CurrentRent) * 100
    const bestOffer = row.bkin_offers
      ? orderBy(row.bkin_offers, 'Rent', 'asc')[0].Rent
      : null;

    let diff = bestOffer
      ? Math.round(((bestOffer - row.Rent) / row.Rent) * 100)
      : 0;

    let doCalc = (d: any, row: any) => {
      d.count++;
      d.total_current_rent += parseInt(row.Rent);
      d.total_market_rent += parseInt(row.Market_Rent);
      d.total_offer_rent +=
        row.bkin_offers && row.bkin_offers.length
          ? bestOffer
          : d.avg_offer_rent;
      d.total_renewal_probability += get(
        row.bkin_offers[0],
        'RenewalProbability'
      );
      d.avg_current_rent = d.total_current_rent / d.count;
      d.avg_market_rent = d.total_market_rent / d.count;
      d.avg_offer_rent = d.total_offer_rent / d.count;
      d.diff_offer_vs_current = (
        ((d.avg_offer_rent - d.avg_current_rent) / d.avg_current_rent) *
        100
      ).toFixed(1);
      d.diff_offer_vs_market = (
        ((d.avg_offer_rent - d.avg_market_rent) / d.avg_market_rent) *
        100
      ).toFixed(1);
      return d;
    };

    if (row.bkin_offers) {
      if (diff < 2) {
        data['<2%'] = doCalc(data['<2%'], row);
        updateRangeMapping(SummaryTableDataFields['<2%'], row.Tenant_Code);
        row = { ...row, group: 1 };
      } else if (diff >= 2 && diff <= 4) {
        data['2-4%'] = doCalc(data['2-4%'], row);
        updateRangeMapping(SummaryTableDataFields['2-4%'], row.Tenant_Code);
        row = { ...row, group: 2 };
      } else if (diff > 4 && diff <= 6) {
        data['4-6%'] = doCalc(data['4-6%'], row);
        updateRangeMapping(SummaryTableDataFields['4-6%'], row.Tenant_Code);
        row = { ...row, group: 3 };
      } else if (diff > 6 && diff <= 8) {
        data['6-8%'] = doCalc(data['6-8%'], row);
        updateRangeMapping(SummaryTableDataFields['6-8%'], row.Tenant_Code);
        row = { ...row, group: 4 };
      } else if (diff > 8) {
        data['>8%'] = doCalc(data['>8%'], row);
        updateRangeMapping(SummaryTableDataFields['>8%'], row.Tenant_Code);
        row = { ...row, group: 5 };
      }
    }
  };

  let tableData: any = {};
  ['<2%', '2-4%', '4-6%', '6-8%', '>8%'].forEach((e) => {
    tableData[e] = {
      count: 0,
      diff: 0,
      total_market_rent: 0,
      total_current_rent: 0,
      total_offer_rent: 0,
      total_renewal_probability: 0,
      avg_market_rent: 0,
      avg_current_rent: 0,
      avg_offer_rent: 0,
      diff_offer_vs_market: 0,
      diff_offer_vs_current: 0,
    };
  });

  dashboardData.forEach((elem, i: number) => {
    if (selectedProperty === ALL) {
      getValues(tableData, elem);
    } else if (String(selectedProperty) === String(elem.Property)) {
      getValues(tableData, elem);
    }
  });
  return { summaryData: tableData, rangeMapping };
};

const dashboardReducer = (state = initialState, action: AnyAction) => {
  switch (action.type) {
    case 'SEARCH_RESULTS': {
      return produce(state, (draft) => {
        draft.searchResults = action.payload;
      });
    }
    case 'DASHBOARD_DATA': {
      let { summaryData, rangeMapping } = calculateSummaryTableData(
        action.payload.original,
        ALL
      );

      return produce(state, (draft) => {
        draft.dashboardData = action.payload.original;
        draft.summaryData = summaryData;
        draft.normalized = action.payload.normalized;
        draft.rangeMapping = rangeMapping;
        draft.activeStrategy = action.payload.strategy;
      });
    }
    case 'DASHBOARD_DATA_FAILED': {
      console.log('failed');
      return produce(state, (draft) => {
        draft.dashboardData = [];
        draft.summaryData = [];
      });
    }
    case 'APPROVE_ALL': {
      return produce(state, (draftState) => {
        action.payload.tcodes.forEach((tcode: string) => {
          draftState.normalized?.entities.tenants[tcode].bkin_offers.forEach(
            (id: string) => {
              if (
                draftState.normalized &&
                !draftState.normalized.entities.offers[id].Exclude
              )
                draftState.normalized.entities.offers[id].IsApproved =
                  action.payload.checked;
            }
          );
          if (
            draftState.normalized &&
            !draftState.normalized.entities.tenants[tcode].Exclude
          )
            draftState.normalized.entities.tenants[tcode].IsApproved =
              action.payload.checked;
        });
      });
    }

    case 'FETCH_STRATEGY': {
      return produce(state, (draft) => {
        draft.selectedStrategy = action.payload;
      });
    }

    case 'UPDATE_SUMMARY_DATA': {
      if (state.dashboardData) {
        let { summaryData } = calculateSummaryTableData(
          state.dashboardData,
          action.payload
        );
        return produce(state, (draft) => {
          draft.summaryData = summaryData;
        });
      }
      return state;
    }
    case 'KPI_DATA': {
      return Object.assign({}, state, { kpiData: action.payload });
    }
    case 'SELECTED_TENANT': {
      if (!action.payload)
        return Object.assign({}, state, {
          selectedTenant: action.payload,
        });
      return Object.assign({}, state, {
        selectedTenant: action.payload ? action.payload.Tenant_Code : undefined,
      });
    }

    // case 'SELECTED_SEARCH_RESULT': {
    //     break;
    // }
    case 'HANDLE_APPROVE': {
      return produce(state, (draftState) => {
        let tenant = draftState.normalized?.entities.tenants[action.payload];
        if (tenant.bkin_offers) {
          tenant.bkin_offers.forEach((offerId: string) => {
            if (draftState.normalized?.entities.offers[offerId]) {
              draftState.normalized.entities.offers[offerId].IsApproved = true;
              draftState.normalized.entities.offers[offerId].Exclude = false;
            }
          });
        }

        tenant.IsApproved = true;
        tenant.Exclude = false;
      });
    }

    case 'HANDLE_DISAPPROVE': {
      return produce(state, (draftState) => {
        let tenant = draftState.normalized?.entities.tenants[action.payload];
        if (tenant.bkin_offers) {
          tenant.bkin_offers.forEach((offerId: string) => {
            if (draftState.normalized?.entities.offers[offerId])
              draftState.normalized.entities.offers[offerId].IsApproved = false;
          });
        }
        tenant.IsApproved = false;
      });
    }
    case 'ADD_OFFER': {
      return produce(state, (draftState) => {
        let offer = action.payload;
        let tenant = draftState.normalized?.entities.tenants[offer.Tenant_Code];
        if (tenant) {
          tenant.bkin_offers.push(offer.id);
          if (draftState.normalized)
            draftState.normalized.entities.offers[offer.id] = offer;
        }
      });
    }
    case 'ADD_OTHER_CHARGES': {
      return produce(state, (draftState) => {
        let tenant =
          draftState.normalized?.entities.tenants[action.payload.Tenant_Code];

        if (tenant) {
          if (!tenant.other_charges) {
            tenant.other_charges = [];
          }
          tenant.other_charges.push(action.payload.id);
          if (draftState.normalized)
            draftState.normalized.entities.charges[action.payload.id] =
              action.payload;
        }
      });
    }
    case 'REMOVE_CHARGE': {
      return produce(state, (draftState) => {
        let tenant =
          draftState.normalized?.entities.tenants[action.payload.Tenant_Code];
        if (tenant) {
          //TODO removes the deleted charge from dashboard. in future do a soft delete for an undo action.
          tenant.other_charges = tenant.other_charges.filter(
            (id: string) => id !== action.payload.chargeId
          );
          if (draftState.normalized)
            delete draftState.normalized.entities.charges[
              action.payload.chargeId
            ];
        }
      });
    }

    case 'IS_LOADING': {
      return produce(state, (draft) => {
        draft.isLoading = action.payload;
      });
    }

    case 'SEARCH_OPTIONS': {
      return produce(state, (draft) => {
        draft.searchResults = action.payload.map((e: any) => ({
          title: e.Tenant_Code,
          description: 'Tenant Code',
        }));
      });
    }
    case 'SEARCH_RESULT': {
      return produce(state, (draft) => {
        draft.selectedSearchResult = action.payload;
        draft.searchResults = action.payload ? state.searchResults : [];
      });
    }
    case 'UPDATE_OFFER': {
      return produce(state, (draftState) => {
        let { offerid, new_rent } = action.payload;
        if (draftState.normalized?.entities.offers) {
          draftState.normalized.entities.offers[offerid].Rent = new_rent;

          draftState.normalized.entities.offers[offerid].IsUpdated = true;
        }
      });
    }
    case 'CHARGE_CODES': {
      return produce(state, (draft) => {
        draft.chargeOptions = action.payload;
      });
    }
    case 'SELECTED_RANGE': {
      return produce(state, (draft) => {
        draft.rangeFilter = action.payload;
      });
    }
    case 'TOGGLE_EXCLUDE': {
      return produce(state, (draftState) => {
        let { tenant_code, exclude } = action.payload;
        let tenant = draftState.normalized?.entities.tenants[tenant_code];
        if (tenant.bkin_offers) {
          tenant.bkin_offers.forEach((offerId: string) => {
            if (draftState.normalized?.entities.offers[offerId]) {
              draftState.normalized.entities.offers[offerId].Exclude = exclude;
              draftState.normalized.entities.offers[offerId].IsApproved = false;
            }
          });
        }
        tenant.Exclude = exclude;
        tenant.IsApproved = false;
      });
    }
    case 'UPDATE_FLAG': {
      return produce(state, (draftState) => {
        let { tenant_code, flag_details } = action.payload;
        let tenant = draftState.normalized?.entities.tenants[tenant_code];
        tenant.flag_details = flag_details.id;
        tenant.IsFlagged = !flag_details.Resolved;
        if (draftState.normalized?.entities.flagDetails)
          draftState.normalized.entities.flagDetails[flag_details.id] =
            flag_details;
        else {
          if (draftState.normalized)
            draftState.normalized.entities.flagDetails = {
              [flag_details.id]: flag_details,
            };
        }
      });
    }
    case 'REMOVE_OFFER': {
      return produce(state, (draftState) => {
        let tenant =
          draftState.normalized?.entities.tenants[action.payload.Tenant_Code];
        if (tenant) {
          //TODO removes the deleted charge from dashboard. in future do a soft delete for an undo action.
          tenant.bkin_offers = tenant.bkin_offers.filter(
            (id: string) => id !== action.payload.offer_id
          );
          if (draftState.normalized)
            delete draftState.normalized.entities.offers[
              action.payload.offer_id
            ];
        }
      });
    }
    case 'REPORTS_DATA': {
      let selectedOfferCycle =
        action.payload &&
        action.payload.length > 0 &&
        (action.payload.find((elem: any) => elem.IsCurrent) ||
          action.payload[0]);
      // let enableEdit = !(selectedOfferCycle && selectedOfferCycle.Uploaded);

      const newState = produce(state, (draft) => {
        draft.offerCycles = action.payload;
        draft.selectedOfferCycle = selectedOfferCycle;
        // draft.enableEdit = enableEdit;
      });

      return newState;
    }
    case 'UPDATE_OFFER_CYCLE': {
      let selectedOfferCycle = state.offerCycles?.filter(
        (e) => e.OfferCycle === action.payload
      )[0];

      return produce(state, (draft) => {
        draft.selectedOfferCycle = selectedOfferCycle;
        // draft.enableEdit = !selectedOfferCycle?.Uploaded;
      });
    }
    case 'UPDATE_CHARGE': {
      return produce(state, (draftState) => {
        let { tenant_code, chargeid, rent } = action.payload;
        let tenant = draftState.normalized?.entities.tenants[tenant_code];
        if (tenant) {
          if (draftState.normalized?.entities.charges) {
            draftState.normalized.entities.charges[chargeid].Rent = rent;
            draftState.normalized.entities.charges[chargeid].IsUpdated = true;
          }
        }
      });
    }
    case 'SET_OFFER_STATE': {
      return produce(state, (draft) => {
        draft.selectedOfferFilter = action.payload;
      });
    }
    case 'SET_SELECTED_VIEW': {
      return produce(state, (draft) => {
        draft.selectedView = action.payload;
      });
    }
    case 'SET_SHOW_ADD_OFFER_DIALOG': {
      return {
        ...state,
        showAddOfferDialog: action.payload.show,
        selectedTenant: action.payload.selectedTenant,
      };
    }
    case 'SET_SHOW_EDIT_OFFER_DIALOG': {
      return {
        ...state,
        showAddOfferDialog: action.payload.show,
        selectedTenant: action.payload.selectedTenant,
        offerEditMode: action.payload.offerId
          ? {
              offerId: action.payload.offerId,
            }
          : undefined,
      };
    }
    case 'SET_SHOW_FILTER_SETTINGS': {
      return produce(state, (draft) => {
        draft.showFilterSettingsDrawer = action.payload;
      });
    }

    case 'SET_STICKINESS_FILTER': {
      return produce(state, (draft) => {
        if (draft.stickinessFilterState === action.payload)
          draft.stickinessFilterState = undefined;
        else draft.stickinessFilterState = action.payload;
      });
    }
    case 'GET_RENEWAL_SUMMARY': {
      return produce(state, (df) => {
        df.renewalSummary = action.payload;
      });
    }

    case 'RENEWAL_UPDATE_FLAG': {
      const flagItem = action.payload as TenantFlagItem;
      return produce(state, (df) => {
        if (
          df.normalized?.entities?.tenants &&
          df.normalized?.entities?.tenants[flagItem.tenant.Tenant_Code]
        ) {
          const tenant = {
            ...df.normalized?.entities?.tenants[flagItem.tenant.Tenant_Code],
          };
          tenant.flag_details = flagItem.id;
          tenant.IsFlagged = !flagItem.resolved;
          df.normalized.entities.tenants[flagItem.tenant.Tenant_Code] = tenant;
        }
      });
    }

    case 'RENEWAL_UPDATE_SUMMARY_DATA': {
      if (!state.dashboardData || state.dashboardData?.length === 0)
        return state;
      const { property, bedroom } = action.payload;
      const filteredData = state.dashboardData.filter(
        (el: any) =>
          (String(bedroom).toUpperCase() === 'ALL'
            ? true
            : el.BedRooms === Number(bedroom)) &&
          (String(property).toUpperCase() === 'ALL'
            ? true
            : String(el.Property) === String(property))
      );
      let { summaryData } = calculateSummaryTableData(filteredData, 'ALL');
      return produce(state, (draft) => {
        draft.summaryData = summaryData;
      });
    }

    case 'INITIALIZE': {
      return state;
    }
    case 'LOGOUT': {
      return initialState;
    }

    // case 'OPEN_UPLOAD_TO_PMS_MODAL': {
    //   return produce(state, (draft) => {
    //     draft.uploadToPMS.isOpen = true;
    //   });
    // }

    // case 'CLOSE_UPLOAD_TO_PMS_MODAL': {
    //   return produce(state, (draft) => {
    //     draft.uploadToPMS.isOpen = false;
    //   });
    // }

    case 'SYNC_OFFERS_TO_UPLOAD': {
      return produce(state, (draft) => {
        draft.uploadToPMS.data = action.payload.data;
      });
    }

    case 'UPLOAD_TO_PMS_IS_LOADING': {
      return produce(state, (draft) => {
        draft.uploadToPMS.isLoading = action.payload.isLoading;
      });
    }

    default: {
      return state;
    }
  }
};
export default dashboardReducer;
